import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { Button, type ButtonProps } from '@vs-shop/ui/button';
import { clsxm } from '@vs-shop/utils';

/**
 * What a page says where a list, a field or a panel would have been:
 *
 *   <EmptyState variant="stated">
 *     <EmptyStateBody>
 *       <EmptyStateTitle>Nenašli jsme žádné články</EmptyStateTitle>
 *       <EmptyStateDescription>Zkuste jiné téma.</EmptyStateDescription>
 *     </EmptyStateBody>
 *     <EmptyStateCta>Začněte nakupovat</EmptyStateCta>
 *   </EmptyState>
 *
 *   <EmptyState variant="inline">Nic neodpovídá</EmptyState>
 *
 * **Two shapes, and the platform had them written out six times.** A cleared
 * cart and an empty article listing are the same centred region at the same
 * display scale; a facet with nothing left, a drawer before anything is in it,
 * an empty brand index and an empty product field are the same centred line.
 * The difference between the members of each pair was spacing, which is what a
 * `className` is for.
 *
 * **What it is NOT.** A `<span>` of small print at the end of a row of order
 * meta, the panel that offers a shopper their first favourites list, a search
 * page with no query, and the notice a glossary shares with its own error state
 * are four different things that happen to mean "nothing here", and each stays
 * where it is. The census that decided which is which is
 * `./emptyState.stories.tsx`.
 *
 * **No heading element.** An empty state is furniture a page places, and a page
 * that wants its title in the outline composes `@vs-shop/ui/heading` itself.
 *
 * Server-first: every part here is a plain component.
 */

/**
 * How loudly a page says it.
 *
 * `stated` fills the place the missing thing would have taken, at the display
 * scale a page speaks in. `inline` is one line where a run of items would have
 * been, taking only the room a line needs. Neither states its own spacing: how
 * much room a place is given is the page's, and it is the one thing that
 * differed between the call sites this replaced.
 */
const emptyStateVariants = cva('', {
  variants: {
    variant: {
      /** A region of its own, centred, at the page's display scale. */
      stated:
        'font-heading text-foreground lap:text-3xl flex w-full flex-col items-center justify-center text-xl',
      /** One centred line where a run of items would have been. */
      inline: 'flex items-center justify-center',
    },
  },
});

type EmptyStateVariantName = NonNullable<VariantProps<typeof emptyStateVariants>['variant']>;

export type EmptyStateVariant = EmptyStateVariantName;

/**
 * The appearance axis, bound to the recipe by `satisfies` so the two cannot
 * drift. The `satisfies` clause names the private alias: an exported one shares
 * its identifier with this const, and react-docgen follows that cycle until its
 * stack runs out.
 */
export const EmptyStateVariant = {
  /** A region of its own. */
  STATED: 'stated',
  /** One line. */
  INLINE: 'inline',
} as const satisfies Record<string, EmptyStateVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type EmptyStateProps = ComponentProps<'div'> & {
  readonly variant: EmptyStateVariant;
};

/** What stands where the missing thing would have been. */
export const EmptyState = ({ className, variant, ...props }: EmptyStateProps) => (
  <div
    data-slot="empty-state"
    className={clsxm(emptyStateVariants({ variant }), className)}
    {...props}
  />
);

export type EmptyStateTextProps = ComponentProps<'span'>;

/**
 * The sentence an inline note carries, set quiet against the ground.
 *
 * A part rather than a `<span>` the composition paints, because the one thing
 * every inline note has in common is that it is quieter than what surrounds it,
 * and a composition that had to remember that would eventually not.
 */
export const EmptyStateText = ({ className, ...props }: EmptyStateTextProps) => (
  <span
    data-slot="empty-state-text"
    className={clsxm('text-foreground-muted', className)}
    {...props}
  />
);

export type EmptyStateBodyProps = ComponentProps<'div'>;

/** The words, held together above whatever the state offers. */
export const EmptyStateBody = ({ className, ...props }: EmptyStateBodyProps) => (
  <div
    data-slot="empty-state-body"
    className={clsxm('flex flex-col items-center gap-2 text-center', className)}
    {...props}
  />
);

export type EmptyStateTitleProps = ComponentProps<'div'>;

/** The line naming what is not there. A neutral element, for the reason above. */
export const EmptyStateTitle = ({ className, ...props }: EmptyStateTitleProps) => (
  <div
    data-slot="empty-state-title"
    className={clsxm('lap:text-3xl text-xl', className)}
    {...props}
  />
);

export type EmptyStateDescriptionProps = ComponentProps<'div'>;

/** What to try instead, quieter than the line above it. */
export const EmptyStateDescription = ({ className, ...props }: EmptyStateDescriptionProps) => (
  <div
    data-slot="empty-state-description"
    className={clsxm('text-foreground-muted lap:text-lg text-base', className)}
    {...props}
  />
);

export type EmptyStateCtaProps = ButtonProps;

/**
 * The way out of the dead end.
 *
 * Its own type size rather than the region's: the words around it run at a
 * display scale, and a control at that scale reads as a second heading. A page
 * handing in its router's link through `render` must do so from a client file.
 */
export const EmptyStateCta = ({ className, ...props }: EmptyStateCtaProps) => (
  <Button
    variant="primary"
    data-slot="empty-state-cta"
    className={clsxm('text-lg', className)}
    {...props}
  />
);
